import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuthContext } from '../context/AuthContext';

const ROLES = ['client', 'coach', 'admin'];
const STATUSES = ['active', 'pending', 'inactive'];

const emptyCreate = {
  name: '',
  email: '',
  role: 'client',
  status: 'active',
  password: '',
};

const AdminUsers = () => {
  const { user } = useAuthContext();
  const [users, setUsers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createForm, setCreateForm] = useState(emptyCreate);
  const [createLoading, setCreateLoading] = useState(false);

  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: 'client',
    status: 'active',
    new_password: '',
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [resendLoadingId, setResendLoadingId] = useState(null);
  const [assignCoachByClientId, setAssignCoachByClientId] = useState({});
  const [assignLoadingId, setAssignLoadingId] = useState(null);

  const isAdmin = user?.role === 'admin';

  const loadUsers = useCallback(async (nextSearch = search, nextRole = roleFilter) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (nextSearch.trim()) params.search = nextSearch.trim();
      if (nextRole !== 'all') params.role = nextRole;
      const res = await api.get('/admin/users', { params });
      setUsers(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter]);

  const loadCoaches = useCallback(async () => {
    try {
      const res = await api.get('/admin/users', { params: { role: 'coach' } });
      setCoaches(res.data || []);
    } catch {
      setCoaches([]);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadUsers();
    loadCoaches();
  }, [isAdmin, loadUsers, loadCoaches]);

  const counts = useMemo(() => {
    const summary = { total: users.length, admin: 0, coach: 0, client: 0 };
    users.forEach((u) => {
      if (summary[u.role] != null) summary[u.role] += 1;
    });
    return summary;
  }, [users]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setError('');
    try {
      await api.post('/admin/users', createForm);
      setCreateForm(emptyCreate);
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo crear usuario');
    } finally {
      setCreateLoading(false);
    }
  };

  const beginEdit = (u) => {
    setEditingUserId(u.id);
    setEditForm({
      name: u.name,
      email: u.email,
      role: u.role,
      status: u.status,
      new_password: '',
    });
  };

  const cancelEdit = () => {
    setEditingUserId(null);
    setEditForm({ name: '', email: '', role: 'client', status: 'active', new_password: '' });
  };

  const saveEdit = async (id) => {
    setSaveLoading(true);
    setError('');
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        status: editForm.status,
      };
      if (editForm.new_password.trim()) payload.new_password = editForm.new_password.trim();
      await api.put(`/admin/users/${id}`, payload);
      cancelEdit();
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo actualizar usuario');
    } finally {
      setSaveLoading(false);
    }
  };

  const resendActivationEmail = async (targetUserId) => {
    setResendLoadingId(targetUserId);
    setError('');
    try {
      await api.post(`/admin/users/${targetUserId}/resend-activation-email`);
      toast.success('Correo de activación reenviado');
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo reenviar correo de activación');
    } finally {
      setResendLoadingId(null);
    }
  };

  const assignCoach = async (clientId) => {
    const coachId = Number(assignCoachByClientId[clientId] || 0);
    if (!coachId) {
      setError('Selecciona un coach para asignar al cliente');
      return;
    }
    setAssignLoadingId(clientId);
    setError('');
    try {
      await api.put(`/admin/clients/${clientId}/assign-coach`, { coach_id: coachId });
      toast.success('Cliente asignado al coach');
      await loadUsers(search, roleFilter);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo asignar coach');
    } finally {
      setAssignLoadingId(null);
    }
  };

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container mx-auto p-4 md:p-8 space-y-6 fade-up">
      <div>
        <h1 className="text-3xl md:text-4xl font-display font-bold text-white">Gestion de Usuarios</h1>
        <p className="text-gray-400 text-sm mt-1">Solo administradores: crear y modificar usuarios existentes.</p>
      </div>

      {error && (
        <p className="text-red-300 bg-red-950/40 border border-red-900/60 rounded-lg px-3 py-2 text-sm">{error}</p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-panel rounded-xl p-4 text-center"><p className="text-2xl font-bold text-white">{counts.total}</p><p className="text-xs text-gray-400">Total</p></div>
        <div className="glass-panel rounded-xl p-4 text-center"><p className="text-2xl font-bold text-purple-300">{counts.admin}</p><p className="text-xs text-gray-400">Admins</p></div>
        <div className="glass-panel rounded-xl p-4 text-center"><p className="text-2xl font-bold text-marizan-red">{counts.coach}</p><p className="text-xs text-gray-400">Coaches</p></div>
        <div className="glass-panel rounded-xl p-4 text-center"><p className="text-2xl font-bold text-green-300">{counts.client}</p><p className="text-xs text-gray-400">Clientes</p></div>
      </div>

      <form onSubmit={handleCreateUser} className="glass-panel rounded-xl p-5 space-y-4">
        <h2 className="text-white font-semibold">Crear Usuario</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="bg-gray-800/80 border border-gray-600 text-white text-sm rounded-lg p-2.5" placeholder="Nombre" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} required />
          <input className="bg-gray-800/80 border border-gray-600 text-white text-sm rounded-lg p-2.5" placeholder="Correo" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} required />
          <input className="bg-gray-800/80 border border-gray-600 text-white text-sm rounded-lg p-2.5" placeholder="Contraseña" type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} required />
          <div className="grid grid-cols-2 gap-2">
            <select className="bg-gray-800/80 border border-gray-600 text-white text-sm rounded-lg p-2.5" value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="bg-gray-800/80 border border-gray-600 text-white text-sm rounded-lg p-2.5" value={createForm.status} onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" disabled={createLoading} className="bg-marizan-red hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg disabled:bg-gray-600">
          {createLoading ? 'Creando...' : 'Crear Usuario'}
        </button>
      </form>

      <div className="glass-panel rounded-xl p-5 space-y-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <h2 className="text-white font-semibold">Usuarios Existentes</h2>
          <div className="flex gap-2">
            <input
              className="bg-gray-800/80 border border-gray-600 text-white text-sm rounded-lg p-2.5"
              placeholder="Buscar por nombre o correo"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select className="bg-gray-800/80 border border-gray-600 text-white text-sm rounded-lg p-2.5" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">Todos</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button type="button" onClick={() => loadUsers(search, roleFilter)} className="bg-gray-700 hover:bg-gray-600 text-white rounded-lg px-3 py-2 text-sm">Filtrar</button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="skeleton h-12 rounded" />)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-300">
              <thead className="text-xs uppercase text-gray-400 border-b border-white/10">
                <tr>
                  <th className="py-3 pr-3">ID</th>
                  <th className="py-3 pr-3">Nombre</th>
                  <th className="py-3 pr-3">Correo</th>
                  <th className="py-3 pr-3">Rol</th>
                  <th className="py-3 pr-3">Estado</th>
                  <th className="py-3 pr-3">Coach Asignado</th>
                  <th className="py-3 pr-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/5">
                    <td className="py-3 pr-3">{u.id}</td>
                    <td className="py-3 pr-3">{editingUserId === u.id ? <input className="bg-gray-800 border border-gray-600 text-white rounded px-2 py-1 w-full" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /> : u.name}</td>
                    <td className="py-3 pr-3">{editingUserId === u.id ? <input className="bg-gray-800 border border-gray-600 text-white rounded px-2 py-1 w-full" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /> : u.email}</td>
                    <td className="py-3 pr-3">
                      {editingUserId === u.id ? (
                        <select className="bg-gray-800 border border-gray-600 text-white rounded px-2 py-1" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : u.role}
                    </td>
                    <td className="py-3 pr-3">
                      {editingUserId === u.id ? (
                        <select className="bg-gray-800 border border-gray-600 text-white rounded px-2 py-1" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : u.status}
                    </td>
                    <td className="py-3 pr-3">
                      {u.role === 'client' ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <select
                            className="bg-gray-800 border border-gray-600 text-white rounded px-2 py-1"
                            value={assignCoachByClientId[u.id] ?? (u.assigned_coach_id || '')}
                            onChange={(e) => setAssignCoachByClientId((prev) => ({ ...prev, [u.id]: e.target.value }))}
                          >
                            <option value="">Sin coach</option>
                            {coaches.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => assignCoach(u.id)}
                            disabled={assignLoadingId === u.id}
                            className="bg-amber-700 hover:bg-amber-600 disabled:bg-gray-600 text-white rounded px-2 py-1"
                          >
                            {assignLoadingId === u.id ? 'Asignando...' : 'Asignar'}
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      {editingUserId === u.id ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <input className="bg-gray-800 border border-gray-600 text-white rounded px-2 py-1" placeholder="Nueva contraseña (opcional)" value={editForm.new_password} onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })} />
                          <button type="button" onClick={() => saveEdit(u.id)} disabled={saveLoading} className="bg-green-700 hover:bg-green-600 text-white rounded px-2 py-1">Guardar</button>
                          <button type="button" onClick={cancelEdit} className="bg-gray-700 hover:bg-gray-600 text-white rounded px-2 py-1">Cancelar</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <button type="button" onClick={() => beginEdit(u)} className="bg-blue-700 hover:bg-blue-600 text-white rounded px-2 py-1">Editar</button>
                          <button
                            type="button"
                            onClick={() => resendActivationEmail(u.id)}
                            disabled={resendLoadingId === u.id}
                            className="bg-rose-700 hover:bg-rose-600 disabled:bg-gray-600 text-white rounded px-2 py-1"
                          >
                            {resendLoadingId === u.id ? 'Enviando...' : 'Reenviar activación'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
